/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import "./Login.css";
import { useNavigate } from "react-router-dom";
import skincareImage from "../images/skincare.png";
import { clearSessionExpiredFlag } from "../utils/sessionBus";
import EmailVerification from "./EmailVerification";
import { GoogleLogin } from "@react-oauth/google"; // Import the GoogleLogin component
import { jwtDecode } from "jwt-decode"; // Import the JWT decode library
import {
 getProfileInfo,
 googleSignIn,
 facebookSignIn,
 appleSignIn,
 requestVerificationCode,
} from "../api/auth";
import Icons from "./Icons";
import { checkDailyCheckIn } from "../components/xpUtils";
/* global FB */
const Login = () => {
const [inputs, setInputs] = useState({});
const [showVerification, setShowVerification] = useState(false);
const [errorMessage, setErrorMessage] = useState("");
const navigate = useNavigate();
const [isButtonDisabled, setIsButtonDisabled] = useState(true);
const [isLoading, setIsLoading] = useState(false);

// Environment variables
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://dev-api.curu.com.au";
const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || "997911069179651";
const APPLE_CLIENT_ID =
  process.env.REACT_APP_APPLE_CLIENT_ID || "TYXV9G44FQ.com.curu.au";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === "true";
const TEST_USER_EMAIL = process.env.REACT_APP_TEST_USER_EMAIL;
const configureAppleSignIn = useCallback(() => {
const script = document.createElement("script");
  script.src ="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
  script.async = true;
  script.onload = () => {
    if (window.AppleID) {
       try {
         // Support multiple development environments
        const currentOrigin = window.location.origin;
        const hostname = window.location.hostname;
        let redirectURI;
        if (process.env.NODE_ENV === "development") {
           // Support localhost and Curu dev domains
          if (
            hostname.includes("localhost") ||
            hostname.includes("127.0.0.1")
          ){
            redirectURI = currentOrigin;
          } else if (hostname.includes("dev-web.curu.com.au")) {
            redirectURI = "https://dev-web.curu.com.au";
          } else {
            redirectURI = currentOrigin; // Try with current origin anyway
          }
        } else {
          redirectURI = `${API_BASE_URL}/api/auth/apple/callback`;
        }
        const config = {
          clientId: APPLE_CLIENT_ID,
          scope: "email name",
          redirectURI: redirectURI,
          usePopup: true,
        };
        window.AppleID.auth.init(config);
        if (DEBUG_AUTH)
          console.log("Apple Sign-In SDK initialized with:", {
            clientId: APPLE_CLIENT_ID,
            redirectURI: redirectURI,
            currentOrigin: currentOrigin,
            hostname: hostname,
          });
      } catch (error) {
        console.error("Failed to initialize Apple Sign-In:", error);
      }
    } else {
      console.error("Apple Sign-In SDK did not load properly");
    }
  };
  script.onerror = () => {
    console.error("Failed to load Apple Sign-In SDK");
  };
  document.head.appendChild(script);
}, [API_BASE_URL, APPLE_CLIENT_ID, DEBUG_AUTH]);


// Prevent the "Session expired" pop-up window from mistakenly popping up on the login page
useEffect(() => {
  clearSessionExpiredFlag();
}, []);


useEffect(() => {
  // Initialize Facebook SDK
  window.fbAsyncInit = function () {
    if (window.FB) {
      FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: "v18.0",
      });
      FB.AppEvents.logPageView();
      if (DEBUG_AUTH)
        console.log("Facebook SDK initialized with App ID:", FB_APP_ID);
    } else {
      console.error(
        "Facebook SDK initialization failed - FB object not available"
      );
    }
  };

  // Load Facebook SDK
  (function (d, s, id) {
    var js,
      fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) return;
    js = d.createElement(s);
    js.id = id;
    js.src = "https://connect.facebook.net/en_US/sdk.js";
    fjs.parentNode.insertBefore(js, fjs);
  })(document, "script", "facebook-jssdk");

  // Initialize Apple Sign-In
  configureAppleSignIn();

  // Auto-fill test email in development
  if (
    process.env.NODE_ENV === "development" &&
    TEST_USER_EMAIL &&
    !inputs.email
  ) {
    setInputs({ email: TEST_USER_EMAIL });
    setIsButtonDisabled(false);
  }
}, [
  DEBUG_AUTH,
  FB_APP_ID,
  TEST_USER_EMAIL,
  configureAppleSignIn,
  inputs.email,
]);

const handleInputChange = (event) => {
  const { name, value } = event.target;
  setInputs((prevInputs) => ({ ...prevInputs, [name]: value }));

  // real-time email validation check and enable/disable login button
  if (name === "email") {
    if (!value) {
      if (DEBUG_AUTH) console.log("Email is required");
      setIsButtonDisabled(true);
      setErrorMessage("Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      if (DEBUG_AUTH) console.log("Email is invalid");
      setIsButtonDisabled(true);
      setErrorMessage("Email is invalid");
    } else {
      setIsButtonDisabled(false);
      setErrorMessage("");
    }
  }
};

const handleNextClick = async () => {
  if (isLoading) return;
  if (!inputs.email) {
    setErrorMessage("Please enter your email");
    return;
  }

  try {
    setIsLoading(true);
    setErrorMessage("");

    if (DEBUG_AUTH) console.log("Attempting to send verification code for:", inputs.email);

    const response = await fetch(`${API_BASE_URL}/api/auth/code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: inputs.email }),
    });

    if (DEBUG_AUTH) console.log("Response status:", response.status);

    setShowVerification(true);
    if (!response.ok) {
      let errMsg = "";
      try {
        const err = await response.json();
        errMsg = err?.message || "";
      } catch {}
      if (DEBUG_AUTH) console.log("Code send not OK:", response.status, errMsg);
      setErrorMessage(errMsg || "We couldn't send a code just now. If you received one, enter it below.");
    }
  } catch (error) {
    console.error("There was a problem sending code:", error);
    setShowVerification(true);
    setErrorMessage("Network issue. If you received a code, enter it below.");
  } finally {
    setIsLoading(false);
  }
};

const handleVerificationClose = () => {
  setShowVerification(false);
};

// Handle profile redirection after successful login
const handleProfileRedirection = async (isGoogleFacebookSignIn, email) => {

  try {
    if (DEBUG_AUTH)
      console.log("Handling profile redirection for email:", email);
    const profileResponse = await getProfileInfo();

    if (profileResponse.ok) {
      const userProfile = await profileResponse.json();
      if (DEBUG_AUTH) console.log("Profile data:", userProfile);

      // If the profile exists and email is attached, redirect to the homepage
      if (userProfile.email) {
        sessionStorage.setItem("userStatus", "old");
        // Check for daily check-in after successful login
        if (DEBUG_AUTH)
          console.log(
            "Login: Checking for daily check-in after successful login..."
          );
        const earnedXP = checkDailyCheckIn();
        if (earnedXP) {

          if (DEBUG_AUTH)
            console.log(
              "Login: User earned daily check-in XP for first login today!"
            );
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("check-xp-earned"));
          }, 1500);
        } else {

          if (DEBUG_AUTH)
            console.log("Login: User already checked in today - no XP popup");
        }
        navigate("/home-page", { state: { email: email } });
      } else {
        sessionStorage.setItem("userStatus", "new");
        navigate("/About", { state: { email: email } });
      }
    } else if (profileResponse.status === 404) {
      sessionStorage.setItem("userStatus", "new");
      navigate("/About", { state: { email: email } });
    } else {
      throw new Error(
        `Failed to fetch profile data: ${profileResponse.statusText}`
      );
    }
  } catch (error) {
    console.error("Error in profile redirection:", error);
    setErrorMessage("Error accessing your profile. Please try again.");
  }
};


const handleVerifyCode = async (code) => {
  setIsLoading(true);
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/auth/token/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: inputs.email,
          verification_code: code,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to verify code");
    }
    const data = await response.json();
    if (DEBUG_AUTH) console.log("Token data:", data);

    // Store the received bearer token
    localStorage.setItem("token", data.access);
    localStorage.setItem("refreshToken", data.refresh);

    await handleProfileRedirection(false, inputs.email);
  } catch (error) {
    console.error("Error during verification:", error);
    setErrorMessage("Failed to verify the code. Please try again.");
  } finally {
    setIsLoading(false);
  }

};


const handleGoogleLoginSuccess = async (credentialResponse) => {
  if (DEBUG_AUTH) console.log("Google Login Response:", credentialResponse);
  setIsLoading(true);

  if (!credentialResponse.credential) {
    console.error("Google login failed: No credential token provided.");
    setErrorMessage("Google login failed. Please try again.");
    setIsLoading(false);
    return;
  }

  try {
    // Decode the JWT to get the user's info directly
    const decodedToken = jwtDecode(credentialResponse.credential);
    const email = decodedToken.email;
    console.log("Google Login Email:", email);
    if (!email) {
      throw new Error("No email found in Google response token");
    }
    const data = await googleSignIn(email);

    if (DEBUG_AUTH) console.log("Google Login successful:", data);
    localStorage.setItem("token", data.access);
    localStorage.setItem("refreshToken", data.refresh);

    await handleProfileRedirection(true, email);
  } catch (error) {
    console.error("Google login error:", error);
    setErrorMessage(`Google login failed: ${error.message}`);
  } finally {
    setIsLoading(false);
  }
};


const handleGoogleLoginFailure = (error) => {
  console.error("Google login failed:", error);
  setErrorMessage(`Google login failed: ${error.message || "Unknown error"}`);
};

 // Facebook login handler


const handleFacebookLogin = () => {
  setIsLoading(true);

  if (!window.FB) {
    console.error("Facebook SDK not loaded");
    setErrorMessage(
      "Facebook login is currently unavailable. Please try again later."
    );
    setIsLoading(false);
    return;
  }

  window.FB.login(
    function (response) {
      if (DEBUG_AUTH) console.log("Facebook login response:", response);
      if (response.authResponse) {
        if (DEBUG_AUTH) console.log("Facebook login successful:", response);

        window.FB.api(
          "/me",
          { fields: "email,name" },
          async function (userInfo) {

            if (DEBUG_AUTH) console.log("Facebook user info:", userInfo);

            if (userInfo.email) {
              try {
                const data = await facebookSignIn(userInfo.email);
                if (DEBUG_AUTH)
                  console.log("Facebook Login successful:", data);
                localStorage.setItem("token", data.access);
                localStorage.setItem("refreshToken", data.refresh);

                await handleProfileRedirection(true, userInfo.email);
              } catch (error) {
                console.error("Facebook login API error:", error);
                setErrorMessage(`Facebook login failed: ${error.message}`);
              } finally {
                setIsLoading(false);
              }
            } else {
              console.error("No email found in Facebook response");
              setErrorMessage(
                "Facebook login failed. Please ensure you allow access to your email."
              );
              setIsLoading(false);
            }
          }
        );
      } else {
        if (DEBUG_AUTH)
          console.log("User cancelled login or did not fully authorise.");

        // Check if it's an app configuration issue
        if (response.error_code === 2800) {
          setErrorMessage(
            "Facebook login is not configured for this domain. Please use Google or email login instead."
          );
        } else {
          setErrorMessage("Facebook login was cancelled or not authorised.");
        }
        setIsLoading(false);
      }
    },
    {
      scope: "email,public_profile",
      return_scopes: true,
    }
  );
};

// Handle Apple Sign-In
const handleAppleSignIn = async () => {
  setIsLoading(true);
  try {

    if (!window.AppleID || !window.AppleID.auth) {
      throw new Error("Apple Sign-In SDK not loaded");
    }

    const response = await window.AppleID.auth.signIn();
    if (DEBUG_AUTH) console.log("Apple Sign-In Response:", response);
    const email = response.user ? response.user.email : null;

    if (!email && !response.authorisation) {
      throw new Error(
        "No email or authorisation code found in Apple response"
      );
    }

    const data = await appleSignIn({
      code: response.authorisation.code,
      id_token: response.authorisation.id_token,
      email: email || `apple_user_${new Date().getTime()}@placeholder.com`,
    });

    if (DEBUG_AUTH) console.log("Apple Login successful:", data);
    localStorage.setItem("token", data.access);
    localStorage.setItem("refreshToken", data.refresh);

    await handleProfileRedirection(true, email);
  } catch (error) {
    console.error("Apple login error:", error);
    if (error.message && error.message.includes("popup_closed_by_user")) {
      setErrorMessage("Apple Sign-In was cancelled.");
    } else if (error.message && error.message.includes("invalid_client")) {
      setErrorMessage(
        "Apple Sign-In is not configured for this domain. Please use Google or email login instead."
      );
    } else {
      setErrorMessage(
        `Apple login failed: ${error.message || "Unknown error"}`
      );
    }
  } finally {
    setIsLoading(false);
  }
};

// Debug function to check environment
const logEnvironmentInfo = useCallback(() => {

  if (process.env.NODE_ENV === "development") {
    console.log("Environment Info:", {
      NODE_ENV: process.env.NODE_ENV,
      API_BASE_URL,
      FB_APP_ID: FB_APP_ID ? `${FB_APP_ID.substring(0, 4)}...` : "Not set",
      APPLE_CLIENT_ID: APPLE_CLIENT_ID
        ? `${APPLE_CLIENT_ID.substring(0, 10)}...`
        : "Not set",
      GOOGLE_CLIENT_ID: GOOGLE_CLIENT_ID ? "Set" : "Not set",
      DEBUG_AUTH,
      TEST_USER_EMAIL: TEST_USER_EMAIL ? "Set" : "Not set",
    });
  }
}, [
  API_BASE_URL,
  FB_APP_ID,
  APPLE_CLIENT_ID,
  GOOGLE_CLIENT_ID,
  DEBUG_AUTH,
  TEST_USER_EMAIL,
]);

// Development testing helpers
const clearAllStorage = () => {
  localStorage.clear();
  sessionStorage.clear();
  console.log("All storage cleared for testing");
};

const simulateNewUser = () => {
  setInputs({ email: `newuser${Date.now()}@test.com` });
  setIsButtonDisabled(false);
  console.log("Simulated new user email generated");
};

// Log environment info on component mount (development only)
useEffect(() => {
  logEnvironmentInfo();
}, [logEnvironmentInfo]);


return (
  <div className="login-container">
    <div className="login-box rounded-5 text-dark text-center bg-white p-5">
      <img src={skincareImage} alt="skincare" className="skincare-image" />
      <p className="h4 fw-bold my-4">Login or Signup</p>
      <p>Welcome to Curu, we're so happy to see you!</p>

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="my-4">
          <input
            type="email"
            placeholder="Enter your email"
            name="email"
            className="form-control form-control-lg w-100"
            value={inputs.email || ""}
            onChange={handleInputChange}
            required
            disabled={isLoading}
          />

          {errorMessage && (
            <p
              className="error-message-login mt-3"
              style={{ fontSize: "14px" }}
            >
              {errorMessage}
            </p>
          )}
        </div>

        <div className="mb-4">
          <button
            type="button"
            className={`btn btn-primary btn-lg text-white w-100 ${
              isButtonDisabled || isLoading ? "disabled" : ""
            }`}
            onClick={handleNextClick}
            disabled={isButtonDisabled || isLoading}
          >
            {isLoading ? "Loading..." : "Next"}
          </button>
        </div>
      </form>

      <p className="divider">OR</p>
      <div className="social-login-container">
        {/* Replaced the button with the GoogleLogin component */}
        <GoogleLogin
          onSuccess={handleGoogleLoginSuccess}
          onError={handleGoogleLoginFailure}
          type="icon"
          shape="circle"
          aria-label="Sign in with Google"
          disabled={isLoading}
        />
        <button
          className="facebook-icon-button border-0"
          onClick={handleFacebookLogin}
          aria-label="Sign in with Facebook"
          disabled={isLoading}
        >
          <Icons.Facebook cn="facebook-icon" width="24" height="24" />
        </button>
              
        {/* Apple login
        <button
          className="apple-icon-button border-0"
          onClick={handleAppleSignIn}
          aria-label="Sign in with Apple"
          disabled={isLoading}
        >
          <Icons.Apple cn="apple-icon" width="24" height="24" />
        </button> */}
      </div>
    </div>
    {showVerification && (
      <EmailVerification
        email={inputs.email}
        onClose={handleVerificationClose}
        onVerify={handleVerifyCode}
        errorMessage={errorMessage}
      />
    )}
  </div>
);
};

export default Login;